import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  AccountDocumentTypeEnum,
  FullBOUserResponseModel,
  MemberHistoryDetailsModel,
  BOUsersBankingInfoTypeEnum,
  BankingInfoInternationalModel,
  BankingInfoDirectDepositModel,
  BankingInfoMailMyChecksResponseModel,
} from 'api/adminApiClient';
import HistoryLog from '../../HistoryLog';
import HistoryField from '../../HistoryField';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getBankingAccountTypeLabel } from 'utils/accountUtils';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import { getRecruiterHistoryFile } from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../../HistoryCard.module.less';

type Props = {
  userId?: string;
  historyDetails: MemberHistoryDetailsModel;
  historyId: string;
};

export default function BankingInformationDetailsCard(props: Props) {
  const { historyDetails, userId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const hasBankingFileKeyChanged = !isEqual(
    oldData?.bankingInformation?.bankInfoFileKey || '',
    newData?.bankingInformation?.bankInfoFileKey || '' || '',
  );

  const handleDownloadBankingFile = async (fileKey?: string) => {
    const result = await dispatch(
      getRecruiterHistoryFile({
        userId,
        fileKey,
        documentType: AccountDocumentTypeEnum.BankingInfo,
      }),
    );

    if (getRecruiterHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderMailTheChecksInfo = (
    mailTheChecks?: BankingInfoMailMyChecksResponseModel,
  ) => {
    const sectionName = 'mail-the-checks';

    return (
      <>
        <Col xs={24}>
          <div className={styles.cardTitle}>Mail the checks</div>
        </Col>

        {!isEqual(
          oldData?.bankingInformation?.mailMyChecksDetail?.payableTo || '',
          newData?.bankingInformation?.mailMyChecksDetail?.payableTo || '',
        ) && (
          <HistoryField
            title="Payable to"
            value={mailTheChecks?.payableTo}
            dataId={`${sectionName}-payable-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.mailMyChecksDetail?.state || '',
          newData?.bankingInformation?.mailMyChecksDetail?.state || '',
        ) && (
          <HistoryField
            title="State"
            value={mailTheChecks?.state}
            dataId={`${sectionName}-state-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.mailMyChecksDetail?.city || '',
          newData?.bankingInformation?.mailMyChecksDetail?.city || '',
        ) && (
          <HistoryField
            title="City"
            value={mailTheChecks?.city}
            dataId={`${sectionName}-city-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.mailMyChecksDetail?.address || '',
          newData?.bankingInformation?.mailMyChecksDetail?.address || '',
        ) && (
          <HistoryField
            title="Address"
            value={mailTheChecks?.address}
            dataId={`${sectionName}-address-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.mailMyChecksDetail?.zip || '',
          newData?.bankingInformation?.mailMyChecksDetail?.zip || '',
        ) && (
          <HistoryField
            title="ZIP code"
            value={mailTheChecks?.zip}
            dataId={`${sectionName}-ZIP-code-field`}
          />
        )}
      </>
    );
  };

  const renderDirectDepositInfo = (
    directDeposit?: BankingInfoDirectDepositModel,
  ) => {
    const sectionName = 'direct-deposit';

    return (
      <>
        <Col xs={24}>
          <div className={styles.cardTitle}>Direct deposit</div>
        </Col>

        {!isEqual(
          oldData?.bankingInformation?.directDepositDetail?.routingNumber || '',
          newData?.bankingInformation?.directDepositDetail?.routingNumber || '',
        ) && (
          <HistoryField
            title="Routing number"
            value={directDeposit?.routingNumber}
            dataId={`${sectionName}-routing-number-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.directDepositDetail?.accountNumber || '',
          newData?.bankingInformation?.directDepositDetail?.accountNumber || '',
        ) && (
          <HistoryField
            title="Account number"
            value={directDeposit?.accountNumber}
            dataId={`${sectionName}-account-number-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.directDepositDetail?.holderName || '',
          newData?.bankingInformation?.directDepositDetail?.holderName || '',
        ) && (
          <HistoryField
            title="Bank account holder name"
            value={directDeposit?.holderName}
            dataId={`${sectionName}-holder-name-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.directDepositDetail?.bankName || '',
          newData?.bankingInformation?.directDepositDetail?.bankName || '',
        ) && (
          <HistoryField
            title="Bank name"
            value={directDeposit?.bankName}
            dataId={`${sectionName}-bank-name-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.directDepositDetail?.accountType || '',
          newData?.bankingInformation?.directDepositDetail?.accountType || '',
        ) && (
          <HistoryField
            title="Account type"
            value={getBankingAccountTypeLabel(directDeposit?.accountType)}
            dataId={`${sectionName}-account-type-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.directDepositDetail?.ddEmailAddress ||
            '',
          newData?.bankingInformation?.directDepositDetail?.ddEmailAddress ||
            '',
        ) && (
          <HistoryField
            title="Direct deposit email address"
            value={directDeposit?.ddEmailAddress}
            dataId={`${sectionName}-dd-email-address-field`}
          />
        )}
      </>
    );
  };

  const renderInternationalInfo = (
    international?: BankingInfoInternationalModel,
  ) => {
    const sectionName = 'international';

    return (
      <>
        <Col xs={24}>
          <div className={styles.cardTitle}>International</div>
        </Col>

        {!isEqual(
          oldData?.bankingInformation?.internationalDetail?.accountHolderName ||
            '',
          newData?.bankingInformation?.internationalDetail?.accountHolderName ||
            '',
        ) && (
          <HistoryField
            title="Account holder name"
            value={international?.accountHolderName}
            dataId={`${sectionName}-account-holder-name-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.internationalDetail
            ?.internationalBankName || '',
          newData?.bankingInformation?.internationalDetail
            ?.internationalBankName || '',
        ) && (
          <HistoryField
            title="Bank name"
            value={international?.internationalBankName}
            dataId={`${sectionName}-bank-name-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.internationalDetail
            ?.internationalAccountNumber || '',
          newData?.bankingInformation?.internationalDetail
            ?.internationalAccountNumber || '',
        ) && (
          <HistoryField
            title="Account number"
            value={international?.internationalAccountNumber}
            dataId={`${sectionName}-account-number-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.internationalDetail?.swiftCode || '',
          newData?.bankingInformation?.internationalDetail?.swiftCode || '',
        ) && (
          <HistoryField
            title="Bank swift code"
            value={international?.swiftCode}
            dataId={`${sectionName}-swift-code-field`}
          />
        )}

        {!isEqual(
          oldData?.bankingInformation?.internationalDetail
            ?.internationalRoutingCode || '',
          newData?.bankingInformation?.internationalDetail
            ?.internationalRoutingCode || '',
        ) && (
          <HistoryField
            title="International routing code"
            value={international?.internationalRoutingCode}
            dataId={`${sectionName}-international-routing-code-field`}
          />
        )}
      </>
    );
  };

  const renderBankingInformation = (
    recruiterProfile?: FullBOUserResponseModel,
    bankingInformationType?: BOUsersBankingInfoTypeEnum,
  ) => {
    if (!recruiterProfile) return null;

    const { bankingInformation } = recruiterProfile;

    if (!bankingInformation) return null;

    const { directDepositDetail, mailMyChecksDetail, internationalDetail } =
      bankingInformation;

    return (
      <>
        <Row gutter={[10, 10]}>
          {bankingInformationType ===
            BOUsersBankingInfoTypeEnum.DirectDeposit && (
            <>{renderDirectDepositInfo(directDepositDetail)}</>
          )}

          {bankingInformationType ===
            BOUsersBankingInfoTypeEnum.MailMyChecks && (
            <>{renderMailTheChecksInfo(mailMyChecksDetail)}</>
          )}

          {bankingInformationType ===
            BOUsersBankingInfoTypeEnum.International && (
            <>{renderInternationalInfo(internationalDetail)}</>
          )}

          {hasBankingFileKeyChanged && (
            <HistoryField
              emptyText="No file"
              title="Voided check or Direct Deposit"
              value={
                bankingInformation?.bankInfoFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleDownloadBankingFile(
                        bankingInformation?.bankInfoFileKey,
                      )
                    }
                  >
                    {getFileNameWithoutExt(bankingInformation.bankInfoFileName)}
                  </span>
                )
              }
              dataId="banking-information-file-field"
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  return (
    <>
      {/* Pass banking info type of new model in order to display appropriate fields for default old type */}
      {oldData?.bankingInformation?.bankingInformationType ===
      BOUsersBankingInfoTypeEnum.Default ? (
        <HistoryLog
          historyId={historyId}
          oldData={renderBankingInformation(
            oldData,
            newData?.bankingInformation?.bankingInformationType,
          )}
          newData={renderBankingInformation(
            newData,
            newData?.bankingInformation?.bankingInformationType,
          )}
        />
      ) : (
        <HistoryLog
          historyId={historyId}
          oldData={renderBankingInformation(
            oldData,
            oldData?.bankingInformation?.bankingInformationType,
          )}
          newData={renderBankingInformation(
            newData,
            newData?.bankingInformation?.bankingInformationType,
          )}
        />
      )}
    </>
  );
}
